import { cn } from "@/lib/cn";

export type ButtonVariant = "primary" | "secondary" | "danger" | "dangerSoft" | "ghost";
export type ButtonSize = "sm" | "md";

const BASE =
  "inline-flex items-center justify-center rounded-[12px] font-semibold transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed";

const SIZE: Record<ButtonSize, string> = {
  sm: "px-3.5 py-2 text-sm",
  md: "px-6 py-3 text-sm",
};

const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-primary text-white hover:bg-primary-deep",
  secondary: "border text-ink hover:bg-surface-2",
  danger: "text-white",
  dangerSoft: "border hover:bg-surface-2",
  ghost: "text-muted hover:bg-surface-2",
};

/** className 문자열만 필요할 때(예: styled <Link>)에 사용. */
export function buttonClass(
  variant: ButtonVariant = "primary",
  size: ButtonSize = "md",
  className?: string
): string {
  return cn(BASE, SIZE[size], VARIANT[variant], className);
}

/** 토큰 색상이 Tailwind 테마에 없는 variant 는 inline style 로 보강. */
function variantStyle(variant: ButtonVariant): React.CSSProperties {
  switch (variant) {
    case "secondary":
      return { borderColor: "var(--line)" };
    case "dangerSoft":
      return { borderColor: "var(--line)", color: "var(--danger)" };
    case "danger":
      return { background: "var(--danger)" };
    default:
      return {};
  }
}

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
};

export function Button({
  variant = "primary",
  size = "md",
  type = "button",
  className,
  style,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={buttonClass(variant, size, className)}
      style={{ ...variantStyle(variant), ...style }}
      {...rest}
    />
  );
}
